import { useTranslations } from 'use-intl';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { canMove } from '@/lib/reorder';

/**
 * Move this one up, move this one down.
 *
 * Buttons rather than dragging. A drag handle wants a precise grab and a
 * sustained press, which is the one gesture a coach standing at the side of a
 * pitch with a phone in one hand cannot reliably make — and it has no keyboard
 * equivalent without writing one. Two 44px targets work with a thumb, a
 * keyboard and a screen reader, and they say what they do.
 *
 * `label` names the thing being moved, because "Move up" five times in a row
 * tells a screen reader nothing about which row it is on.
 */
export function Reorder({
  index,
  count,
  label,
  onMove,
  className = '',
}: {
  index: number;
  count: number;
  label: string;
  onMove: (to: number) => void;
  className?: string;
}) {
  const t = useTranslations();
  if (count < 2) return null;

  const step = (by: -1 | 1) => {
    const enabled = canMove(index, count, by);
    return (
      <button
        type="button"
        disabled={!enabled}
        aria-label={t(by === -1 ? 'common.moveUpThing' : 'common.moveDownThing', { label })}
        onClick={() => onMove(index + by)}
        className="grid size-11 place-items-center rounded-(--radius-control) text-ink-faint transition-colors hover:bg-surface-sunken hover:text-ink disabled:pointer-events-none disabled:opacity-25"
      >
        {by === -1 ? (
          <ChevronUp aria-hidden="true" className="size-4" />
        ) : (
          <ChevronDown aria-hidden="true" className="size-4" />
        )}
      </button>
    );
  };

  return (
    <div className={`flex shrink-0 items-center ${className}`}>
      {step(-1)}
      {step(1)}
    </div>
  );
}
